<template>
  <b-row>
    <b-col :lg="12">
      <ol class="breadcrumb arr-right mb-0 mb-md-3">
        <li class="breadcrumb-item">
          <router-link
            v-track-click
            id="invoice-list-breadcrumb"
            :to="{ name: 'invoices' }"
            class="headings"
          >
            {{ $t('menu.invoices') }}
          </router-link>
        </li>
        <li class="breadcrumb-item reference-heading">
          {{ invoice.reference }}
        </li>
      </ol>
    </b-col>
    <b-col md="7">
      <page-header :title="invoice.reference" :chipText="stageHeading" />
    </b-col>
    <b-col md="2" class="d-flex justify-content-end align-items-baseline">
      <download-dropdown
        class="mt-3"
        :options="fetchDownloadLinks(invoice)"
        @fireEvent="dropdownEvent"
      />
    </b-col>
    <b-col md="3"> </b-col>
    <b-col :lg="3" :order="1" :order-md="0" class="d-print-none mt-3">
      <dhl-tracking
        v-if="validDhlNumber"
        :dhlNumber="invoice.dhl_tracking_number"
      />
      <containers-widget
        :containerIds="splitContainers"
        :invoiceShippingData="invoice.shipping_data"
        :token="$store.getters.token"
        v-if="splitContainers && !isPaid"
      ></containers-widget>
      <updates :invoice="invoice" />
    </b-col>
    <b-col :lg="6" :order="2" :order-md="1" class="mt-3">
      <div class="invoice-details-section drip-card mb-4">
        <div class="container-fluid p-0">
          <b-tabs class="drip-nav" v-model="tabIndex">
            <b-tab
              :title="$t('invoices.single.tabs.details')"
              :active="!isDocsPending"
              title-item-class="col p-0"
              v-if="!isAdvanced"
            >
              <submitted :invoice="invoice" />
            </b-tab>
            <b-tab
              :active="$route.query.tab === 'documents' || isDocsPending"
              title-item-class="col p-0"
              v-if="!isAdvanced"
            >
              <template #title>
                {{ $t('invoices.single.tabs.documents') }}
                <b-icon-exclamation-circle-fill
                  v-if="discrepantInvoices.length"
                  class="icon-warning ml-1"
                  color="red"
                />
              </template>
              <documents
                :invoice="invoice"
                :discrepant-invoices="discrepantInvoices"
                @update-discrepant-invoices="
                  (data) => $emit('update-discrepant-invoices', data)
                "
                v-model="tabIndex"
                @trigger-esign="$emit('trigger-esign')"
              />
            </b-tab>
            <b-tab
              :title="$t('invoices.single.tabs.summary')"
              active
              title-item-class="col p-0"
              v-if="isAdvanced"
            >
              <invoice-summary :invoice="invoice" />
            </b-tab>
            <b-tab
              :title="$t('invoices.single.tabs.statement')"
              title-item-class="col p-0"
              v-if="isAdvanced"
              class="statement"
            >
              <statement :invoice="invoice" />
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </b-col>
    <b-col :lg="3" :order="0" :order-md="2" :cols="12" class="mt-3">
      <pdc-details
        v-if="showPdcWidget"
        :amount="pdcData.amount"
        :date="pdcData.date"
        :words="pdcData.words"
        class="mb-3"
      />
      <alerts
        :invoice="invoice"
        v-model="tabIndex"
        :show-cta="hasPermission"
        class="mb-3"
      />
    </b-col>
  </b-row>
</template>
<script>
import Submitted from '@/components/invoices/Details/v2/Submitted'
import Documents from '@/components/invoices/Details/v2/Documents'
import InvoiceSummary from '@/components/invoices/Details/v2/Summary'
import Statement from '@/components/invoices/Details/v2/Statement'
import Updates from '@/components/invoices/Details/v2/Updates'
import Alerts from '@/components/Alerts2'
import ContainersWidget from '@/components/invoices/Details/Containers/ContainersWidget/'
import DhlTracking from '@/components/invoices/Details/Containers/trackingV2/DhlTracking'
import invoiceService from '@/services/invoice'
import downloadService from '@/services/download/'
import PdcDetails from '@/components/PdcDetails.vue'
import userService from '@/services/user'
import DownloadDropdown from '@/components/common/DownloadDropdown'
import PageHeader from '@/components/common/PageHeader'

export default {
  data() {
    return {
      tabIndex: 1,
      pdcData: {
        amount: '',
        date: '',
        words: ''
      },
      showPdcWidget: false,
      invoiceLoading: false,
      swiftAvailable: false,
      nocAvailable: false,
      swiftType: false
    }
  },
  props: ['invoice', 'discrepantInvoices'],
  watch: {
    tabIndex(newVal) {
      if (newVal >= 0) {
        let mapTab = []
        if (this.isAdvanced) {
          mapTab = ['Summary', 'Statement']
        } else {
          mapTab = ['Details', 'Documents']
        }
        if (mapTab && mapTab[newVal]) {
          this.$mpTrack(`Invoice details - ${mapTab[newVal]} tab clicked`)
        }
      }
    }
  },
  methods: {
    fetchDownloadLinks(invoice) {
      return [
        {
          text: this.$t('common.invoiceSwift'),
          data: {
            id: invoice.id,
            type: 'swift',
            ref: invoice.reference,
            date:
              this.swiftType === 'advance'
                ? invoice.disbursed_date
                : invoice.settlement_date,
            swiftType: this.swiftType
          },
          disabled: !this.swiftAvailable
        },
        {
          text: this.$t('common.invoiceNoc'),
          data: {
            id: invoice.id,
            type: 'noc',
            ref: invoice.reference,
            date:
              this.swiftType === 'advance'
                ? invoice.disbursed_date
                : invoice.settlement_date,
            swiftType: this.swiftType
          },
          disabled: !this.nocAvailable
        },
        {
          text: this.$t('common.invoiceAdvice'),
          data: {
            id: invoice.id,
            type: 'advice'
          },
          disabled: !parseFloat(invoice.advance_total) > 0
        }
      ]
    },
    downloadInvoiceAdvice(invId) {
      this.$store.dispatch('setDownloadNotification', {
        fileDownloadNotification: true
      })
      return invoiceService
        .downloadInvoice(invId)
        .then((res) => {
          downloadService.downloadPDF(res.data, `invoices-${invId}-advice.pdf`)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
        .catch((e) => {
          console.log(e)
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
        })
    },
    async downloadInvoiceSwiftCopy(invId, invRef, invDate, swiftType) {
      if (invId) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        let res = await invoiceService
          .downloadSwiftCopy('invoices', invId)
          .catch(console.log)
        if (res.status === 204) {
          this.$notify({
            title: 'File not found!',
            type: 'error'
          })
        }
        const isPDF = res.data.type === 'application/pdf'
        let filename = downloadService.createFilename(
          isPDF,
          swiftType,
          invRef,
          invDate
        )
        downloadService.downloadFile(res.data, filename)
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    async downloadNocDocument(invId, invRef, invDate, swiftType) {
      if (invId) {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        let res = await invoiceService
          .downloadNocCopy('invoices', invId)
          .catch(console.log)
        if (res.status === 204) {
          this.$notify({
            title: 'File not found!',
            type: 'error'
          })
        }
        const isPDF = res.data.type === 'application/pdf'
        let filename = downloadService.createFilename(
          isPDF,
          swiftType,
          invRef,
          invDate
        )
        downloadService.downloadFile(res.data, filename)
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: false
        })
      } else {
        this.$notify({
          title: 'No document found!',
          type: 'error'
        })
      }
    },
    dropdownEvent(links) {
      if (links.data && links.data.type === 'advice') {
        this.$mpTrack('Advance invoices - Download Invoice advice')
        this.downloadInvoiceAdvice(links.data.id)
      } else if (links.data && links.data.type === 'noc') {
        this.$mpTrack('Advance invoices - Download Noc Document')
        this.downloadNocDocument(
          links.data.id,
          links.data.ref,
          links.data.date,
          links.data.swiftType
        )
      } else {
        this.$mpTrack('Advance invoices - Download Swift copy')
        this.downloadInvoiceSwiftCopy(
          links.data.id,
          links.data.ref,
          links.data.date,
          links.data.swiftType
        )
      }
    },
    printInvoice() {
      window.print()
    },
    downloadInvoice() {
      return invoiceService
        .downloadInvoice(this.invoice.id)
        .then((res) => {
          downloadService.downloadPDF(
            res.data,
            `invoices-${this.invoice.id}-advice.pdf`
          )
        })
        .catch((e) => {
          console.log(e)
        })
    },
    getPdcData() {
      if (!this.showPdcWidget) {
        userService.fetchAccessToken().then((res1) => {
          invoiceService
            .getPdcDetails(this.$route.params.id, res1.data.token)
            .then((res) => {
              this.showPdcWidget = true
              this.pdcData.amount = res.data.pdc_value.split('INR')[1]
              this.pdcData.date = res.data.pdc_date
              this.pdcData.words = res.data.pdc_value_in_words
            })
        })
      }
    }
  },
  async mounted() {
    if (this.invoice['pdc_applicable?']) {
      const invoiceAdvanced =
        this.invoice &&
        this.invoice.task_flows &&
        this.invoice.task_flows.find(
          ({ metadata }) =>
            metadata && metadata.message_key === 'invoice_advanced'
        )

      if (!invoiceAdvanced) {
        this.getPdcData()
      }
    }
    if (this.invoice) {
      const res = await invoiceService.checkSwiftCopy('invoices', [
        this.invoice.id
      ])
      const res2 = await invoiceService.checkNocCopy(
        'invoices',
        this.invoice.id
      )
      this.swiftType = res.data[this.invoice.id].advances
        ? 'advance'
        : res.data[this.invoice.id].settlement
        ? 'settlement'
        : 'all'
      this.swiftAvailable =
        (res.data &&
          res.data[this.invoice.id] &&
          (res.data[this.invoice.id].advances ||
            res.data[this.invoice.id].settlement)) ||
        false
      this.nocAvailable =
        (res2.data &&
          res2.data[this.invoice.id] &&
          (res2.data[this.invoice.id].advances ||
            res2.data[this.invoice.id].settlement)) ||
        false
    }
  },
  computed: {
    stageHeading() {
      return this.$t('invoiceStage')[this.invoice.stage] || this.invoice.stage
    },
    hasPermission() {
      return this.$store.getters.hasPermission('esign')
    },
    requiresEsign() {
      // todo: return esign required.
      return false
    },
    splitContainers() {
      return this.invoice.container !== null
        ? this.invoice.container.match(/[a-zA-Z]{3}[uU]{1}[0-9]{7}/g)
        : false
    },
    isAdvanced() {
      return this.invoice.advances.length > 0
    },
    isPaid() {
      return ['paid', 'writeoff'].indexOf(this.invoice.status) > -1
    },
    isDocsPending() {
      return this.invoice.stage_human === 'Exporter Docs Pending'
    },
    validDhlNumber() {
      const dhlNumber = this.invoice.dhl_tracking_number
      return dhlNumber && dhlNumber.trim() !== ''
    }
  },
  components: {
    Submitted,
    Documents,
    InvoiceSummary,
    Statement,
    Updates,
    Alerts,
    ContainersWidget,
    DhlTracking,
    PdcDetails,
    DownloadDropdown,
    PageHeader
  }
}
</script>
<style lang="scss" scoped>
.statement {
  outline: none;
}
</style>
